/**
 * DIKDOC HUB v9.9.3 — Identity & Access Center
 * Defensive version: every property access is safe-guarded.
 */

import { useState } from "react";
import {
  Users,
  Shield,
  Key,
  Smartphone,
  Mail,
  Activity,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Eye,
  RefreshCw,
  UserPlus,
  Lock,
  Unlock,
  Fingerprint,
  type LucideIcon,
} from "lucide-react";

/* ============================================================================
   Types
   ========================================================================== */

type Role =
  | "superadmin"
  | "admin"
  | "doctor"
  | "nurse"
  | "lab-tech"
  | "pharmacist"
  | "cashier"
  | "auditor"
  | "patient";

type MFAMethod = "totp" | "ms-authenticator" | "google-authenticator" | "fido2" | "sms" | "email";

interface User {
  id: string;
  email: string;
  fullName: string;
  role: Role;
  tenantId: string;
  enabled: boolean;
  mfaEnrolled: boolean;
  mfaMethods: MFAMethod[];
  lastLogin: string | null;
  createdAt: string;
  failedLogins: number;
}

interface MFARow {
  method: MFAMethod;
  label: string;
  enrolled: number;
  enabled: boolean;
  description: string;
  icon: LucideIcon;
}

/* ============================================================================
   Mock data
   ========================================================================== */

const MOCK_USERS: User[] = [
  { id: "u-001", email: "admin@dikdoc.io", fullName: "Karim Bennani", role: "superadmin", tenantId: "default", enabled: true, mfaEnrolled: true, mfaMethods: ["totp", "fido2"], lastLogin: "2026-06-23T22:14:00Z", createdAt: "2026-01-15T10:00:00Z", failedLogins: 0 },
  { id: "u-002", email: "dr.alaoui@chu-rabat.ma", fullName: "Dr. Fatima Alaoui", role: "doctor", tenantId: "chu-rabat", enabled: true, mfaEnrolled: true, mfaMethods: ["ms-authenticator"], lastLogin: "2026-06-24T07:32:00Z", createdAt: "2026-02-20T14:00:00Z", failedLogins: 0 },
  { id: "u-003", email: "lab.tech@chu-rabat.ma", fullName: "Mohammed El Amrani", role: "lab-tech", tenantId: "chu-rabat", enabled: true, mfaEnrolled: false, mfaMethods: [], lastLogin: "2026-06-24T08:00:00Z", createdAt: "2026-03-10T09:00:00Z", failedLogins: 2 },
  { id: "u-004", email: "pharma@dikdoc.io", fullName: "Sara El Idrissi", role: "pharmacist", tenantId: "default", enabled: true, mfaEnrolled: true, mfaMethods: ["google-authenticator"], lastLogin: "2026-06-23T18:45:00Z", createdAt: "2026-04-05T11:00:00Z", failedLogins: 0 },
  { id: "u-005", email: "cashier@clinic.ma", fullName: "Youssef Tazi", role: "cashier", tenantId: "clinic-kenitra", enabled: true, mfaEnrolled: false, mfaMethods: [], lastLogin: "2026-06-24T07:50:00Z", createdAt: "2026-05-12T08:30:00Z", failedLogins: 0 },
  { id: "u-006", email: "audit@dikdoc.io", fullName: "Audit Bot", role: "auditor", tenantId: "system", enabled: true, mfaEnrolled: true, mfaMethods: ["fido2"], lastLogin: "2026-06-24T08:00:00Z", createdAt: "2026-01-01T00:00:00Z", failedLogins: 0 },
  { id: "u-007", email: "suspended@dikdoc.io", fullName: "Test Suspended", role: "doctor", tenantId: "default", enabled: false, mfaEnrolled: false, mfaMethods: [], lastLogin: null, createdAt: "2026-06-01T00:00:00Z", failedLogins: 12 },
];

const MFA_METHODS: MFARow[] = [
  { method: "totp", label: "TOTP (RFC 6238)", enrolled: 47, enabled: true, description: "Standard Time-based One-Time Password", icon: Smartphone },
  { method: "ms-authenticator", label: "Microsoft Authenticator", enrolled: 23, enabled: true, description: "Push notifications + TOTP fallback", icon: Smartphone },
  { method: "google-authenticator", label: "Google Authenticator", enrolled: 18, enabled: true, description: "TOTP with cloud sync", icon: Smartphone },
  { method: "fido2", label: "FIDO2 / WebAuthn", enrolled: 8, enabled: true, description: "Hardware security keys (YubiKey)", icon: Fingerprint },
  { method: "sms", label: "SMS OTP", enrolled: 5, enabled: false, description: "Deprecated per NIST SP 800-63B", icon: Smartphone },
  { method: "email", label: "Email OTP", enrolled: 3, enabled: true, description: "Email-based one-time code (backup)", icon: Mail },
];

const RBAC_PERMISSIONS: Record<Role, string[]> = {
  superadmin: ["*"],
  admin: ["tenant.manage", "user.invite", "user.edit", "audit.read", "settings.write"],
  doctor: ["patient.read", "patient.write", "encounter.write", "prescription.write", "lab.order"],
  nurse: ["patient.read", "encounter.read", "vitals.write", "medication.administer"],
  "lab-tech": ["lab.read", "lab.write", "lab.validate"],
  pharmacist: ["pharmacy.read", "pharmacy.dispense", "stock.write"],
  cashier: ["billing.read", "billing.write", "claim.submit"],
  auditor: ["audit.read", "report.export"],
  patient: ["self.read", "self.appointment", "self.document"],
};

/* ============================================================================
   Sub-components (all defensive)
   ========================================================================== */

function CardWrap({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={"card-surface rounded-2xl p-5 ring-1 ring-white/10 " + (className || "")}>{children}</div>;
}

function StatusBadge({ status }: { status: "ok" | "warn" | "err" | string }) {
  const map: Record<string, { color: string; bg: string; label: string }> = {
    ok: { color: "text-success", bg: "bg-success/10", label: "OK" },
    warn: { color: "text-[oklch(0.78_0.16_85)]", bg: "bg-[oklch(0.78_0.16_85)]/10", label: "WARN" },
    err: { color: "text-accent", bg: "bg-accent/10", label: "ERR" },
  };
  const s = map[status] || map.ok;
  return (
    <span className={"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium " + s.bg + " " + s.color}>
      <CheckCircle2 className="h-3 w-3" /> {s.label}
    </span>
  );
}

function RoleBadge({ role }: { role: string }) {
  const colors: Record<string, string> = {
    superadmin: "bg-accent/20 text-accent",
    admin: "bg-primary/20 text-primary",
    doctor: "bg-success/20 text-success",
    nurse: "bg-info/20 text-info",
    "lab-tech": "bg-[oklch(0.78_0.16_85)]/20 text-[oklch(0.78_0.16_85)]",
    pharmacist: "bg-[oklch(0.75_0.15_280)]/20 text-[oklch(0.75_0.15_280)]",
    cashier: "bg-[oklch(0.7_0.15_200)]/20 text-[oklch(0.7_0.15_200)]",
    auditor: "bg-muted/20 text-muted-foreground",
    patient: "bg-muted/20 text-muted-foreground",
  };
  const cls = colors[role] || colors.patient;
  return <span className={"rounded-full px-2 py-0.5 text-xs font-medium " + cls}>{role}</span>;
}

function safeString(value: unknown, fallback: string = "—"): string {
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return fallback;
  return String(value);
}

/* ============================================================================
   Main component (fully defensive)
   ========================================================================== */

export default function IdentityAccessView({ onBack }: { onBack?: () => void }) {
  const [filter, setFilter] = useState<string>("all");
  const [activeTab, setActiveTab] = useState<string>("users");

  const users = MOCK_USERS;
  const filteredUsers = users.filter((u) => {
    if (!u) return false;
    if (filter === "mfa-required") return !u.mfaEnrolled && u.enabled;
    if (filter === "disabled") return !u.enabled;
    if (filter === "suspicious") return (u.failedLogins || 0) >= 3;
    return true;
  });

  const stats = {
    total: users.length,
    mfaEnrolled: users.filter((u) => u && u.mfaEnrolled).length,
    enabled: users.filter((u) => u && u.enabled).length,
    suspicious: users.filter((u) => u && (u.failedLogins || 0) >= 3).length,
  };
  const mfaPct = stats.total > 0 ? Math.round((stats.mfaEnrolled / stats.total) * 100) : 0;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Shield className="h-6 w-6" /> Identity & Access Center
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Utilisateurs · RBAC · MFA · Keycloak OIDC · Provisioning · Audit
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status="ok" />
          <button className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 flex items-center gap-1">
            <UserPlus className="h-4 w-4" /> Inviter utilisateur
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <CardWrap>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Utilisateurs totaux</div>
          <div className="text-2xl font-bold mt-1">{stats.total}</div>
          <div className="text-xs text-muted-foreground mt-1">{stats.enabled} actifs</div>
        </CardWrap>
        <CardWrap>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">MFA enrolled</div>
          <div className="text-2xl font-bold mt-1 text-success">{mfaPct}%</div>
          <div className="text-xs text-muted-foreground mt-1">{stats.mfaEnrolled}/{stats.total} — target 100%</div>
        </CardWrap>
        <CardWrap>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Comptes désactivés</div>
          <div className="text-2xl font-bold mt-1 text-[oklch(0.78_0.16_85)]">{users.length - stats.enabled}</div>
          <div className="text-xs text-muted-foreground mt-1">Suspendus ou supprimés</div>
        </CardWrap>
        <CardWrap>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Activité suspecte</div>
          <div className="text-2xl font-bold mt-1 text-accent">{stats.suspicious}</div>
          <div className="text-xs text-muted-foreground mt-1">≥3 échecs en 24h</div>
        </CardWrap>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-white/10">
        {(["users", "mfa", "rbac", "flow", "audit"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setActiveTab(t)}
            className={
              "px-4 py-2 text-sm font-medium border-b-2 transition-colors " +
              (activeTab === t ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")
            }
          >
            {t === "users" && "Utilisateurs"}
            {t === "mfa" && "MFA"}
            {t === "rbac" && "RBAC"}
            {t === "flow" && "Flux"}
            {t === "audit" && "Audit"}
          </button>
        ))}
      </div>

      {/* Users tab */}
      {activeTab === "users" && (
        <CardWrap>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="text-sm font-semibold">Comptes utilisateurs ({filteredUsers.length})</h2>
            <div className="flex items-center gap-2">
              {(["all", "mfa-required", "disabled", "suspicious"] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={
                    "rounded-full px-3 py-1 text-xs font-medium " +
                    (filter === f ? "bg-primary text-primary-foreground" : "bg-white/5 text-muted-foreground hover:bg-white/10")
                  }
                >
                  {f === "all" && "Tous"}
                  {f === "mfa-required" && "MFA requis"}
                  {f === "disabled" && "Désactivés"}
                  {f === "suspicious" && "Suspect"}
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 px-2">Utilisateur</th>
                  <th className="py-2 px-2">Rôle</th>
                  <th className="py-2 px-2">Tenant</th>
                  <th className="py-2 px-2">Statut</th>
                  <th className="py-2 px-2">MFA</th>
                  <th className="py-2 px-2">Dernière connexion</th>
                  <th className="py-2 px-2">Échecs</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => {
                  const name = safeString(u.fullName, "—");
                  const initials = name
                    .split(" ")
                    .filter((n) => n.length > 0)
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase() || "?";
                  return (
                    <tr key={u.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/20 text-primary font-semibold text-xs">
                            {initials}
                          </div>
                          <div>
                            <div className="font-medium">{name}</div>
                            <div className="text-xs text-muted-foreground">{safeString(u.email)}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-2"><RoleBadge role={u.role} /></td>
                      <td className="py-3 px-2 text-xs text-muted-foreground">{safeString(u.tenantId, "—")}</td>
                      <td className="py-3 px-2">
                        {u.enabled ? (
                          <span className="inline-flex items-center gap-1 text-success text-xs">
                            <Unlock className="h-3 w-3" /> Actif
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-accent text-xs">
                            <Lock className="h-3 w-3" /> Désactivé
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-2">
                        {u.mfaEnrolled && u.mfaMethods ? (
                          <div className="flex flex-wrap gap-1">
                            {u.mfaMethods.map((m) => (
                              <span key={m} className="rounded bg-success/10 text-success px-1.5 py-0.5 text-[10px]">
                                {m}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[oklch(0.78_0.16_85)] text-xs">⚠ requis</span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-xs text-muted-foreground">
                        {u.lastLogin ? new Date(u.lastLogin).toLocaleString("fr-FR") : "—"}
                      </td>
                      <td className="py-3 px-2">
                        {(u.failedLogins || 0) >= 3 ? (
                          <span className="text-accent text-xs font-bold">{u.failedLogins}</span>
                        ) : (
                          <span className="text-muted-foreground text-xs">{u.failedLogins || 0}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardWrap>
      )}

      {/* MFA tab */}
      {activeTab === "mfa" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {MFA_METHODS.map((m) => {
            const Icon = m.icon;
            return (
              <CardWrap key={m.method}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/20">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{m.label}</h3>
                      <p className="text-xs text-muted-foreground">{m.description}</p>
                    </div>
                  </div>
                  {m.enabled ? <StatusBadge status="ok" /> : <StatusBadge status="warn" />}
                </div>
                <div className="flex items-center justify-between text-sm">
                  <div>
                    <span className="text-muted-foreground">Enrolled: </span>
                    <span className="font-bold text-lg">{m.enrolled}</span>
                    <span className="text-muted-foreground"> utilisateurs</span>
                  </div>
                  <button className="rounded-lg bg-white/5 px-3 py-1.5 text-xs hover:bg-white/10 flex items-center gap-1">
                    <RefreshCw className="h-3 w-3" /> Configurer
                  </button>
                </div>
              </CardWrap>
            );
          })}
        </div>
      )}

      {/* RBAC tab */}
      {activeTab === "rbac" && (
        <CardWrap>
          <h2 className="text-sm font-semibold mb-4 flex items-center gap-2">
            <Key className="h-4 w-4" /> Matrice RBAC (9 rôles × permissions)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {(Object.keys(RBAC_PERMISSIONS) as Role[]).map((role) => {
              const perms = RBAC_PERMISSIONS[role] || [];
              return (
                <div key={role} className="rounded-lg bg-white/5 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <RoleBadge role={role} />
                    <span className="text-xs text-muted-foreground">
                      {perms.length} {perms[0] === "*" ? "all" : "permissions"}
                    </span>
                  </div>
                  <ul className="text-xs space-y-1">
                    {perms.slice(0, 6).map((p) => (
                      <li key={p} className="font-mono text-foreground/80">
                        • {p}
                      </li>
                    ))}
                    {perms.length > 6 && (
                      <li className="text-muted-foreground">+ {perms.length - 6} more…</li>
                    )}
                  </ul>
                </div>
              );
            })}
          </div>
        </CardWrap>
      )}

      {/* Flow tab */}
      {activeTab === "flow" && (
        <CardWrap className="overflow-x-auto">
          <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
            <Activity className="h-4 w-4" /> Flux d'authentification — User → Keycloak → MFA → DIKDOC
          </h3>
          <div className="min-w-[800px]">
            <div className="rounded-lg bg-white/5 p-4 text-xs font-mono whitespace-pre text-foreground/80">
{`User (browser)
  │
  │ 1. Login (INPE + password)
  ▼
Keycloak OIDC (realm: dikdoc)
  │
  │ 2. MFA challenge
  ▼
MFA Provider (TOTP / MS Auth / Google Auth / FIDO2)
  │
  │ 3. Verify code (RFC 6238, 6 digits, 30s)
  │    or push notification (MS Authenticator)
  │    or hardware key (FIDO2/WebAuthn)
  ▼
Keycloak issues JWT
  │    - sub (user id)
  │    - tenant_id (multi-tenant)
  │    - realm_access.roles
  │    - exp (15min session)
  ▼
DIKDOC API
  │
  │ 4. Verify JWT signature
  │ 5. Extract tenant + roles from claims
  │ 6. ABAC policy evaluation
  │ 7. Authorize / Deny
  ▼
FHIR resources returned`}
            </div>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div className="rounded-lg bg-white/5 p-3">
                <div className="font-semibold text-foreground">1. Authentication</div>
                <div className="text-muted-foreground">OIDC via Keycloak</div>
              </div>
              <div className="rounded-lg bg-white/5 p-3">
                <div className="font-semibold text-foreground">2. MFA Verification</div>
                <div className="text-muted-foreground">TOTP / MS / Google / FIDO2</div>
              </div>
              <div className="rounded-lg bg-white/5 p-3">
                <div className="font-semibold text-foreground">3. Token Issuance</div>
                <div className="text-muted-foreground">JWT with tenant_id + roles</div>
              </div>
              <div className="rounded-lg bg-white/5 p-3">
                <div className="font-semibold text-foreground">4. ABAC Authorization</div>
                <div className="text-muted-foreground">Per-resource policy</div>
              </div>
            </div>
          </div>
        </CardWrap>
      )}

      {/* Audit tab */}
      {activeTab === "audit" && (
        <CardWrap>
          <h2 className="text-sm font-semibold mb-4 flex items-center gap-2">
            <Eye className="h-4 w-4" /> Audit Log — Identity Events (24h)
          </h2>
          <div className="space-y-2 text-sm">
            {[
              { ts: "2026-06-24T08:14:22Z", event: "LOGIN_SUCCESS", user: "dr.alaoui@chu-rabat.ma", ip: "10.0.4.21", mfa: "ms-authenticator" },
              { ts: "2026-06-24T08:12:05Z", event: "MFA_ENROLLED", user: "pharma@dikdoc.io", ip: "10.0.4.45", mfa: "google-authenticator" },
              { ts: "2026-06-24T08:05:33Z", event: "PASSWORD_CHANGED", user: "admin@dikdoc.io", ip: "10.0.4.10", mfa: "fido2" },
              { ts: "2026-06-24T07:58:11Z", event: "LOGIN_FAILED", user: "unknown@evil.com", ip: "185.220.101.42", mfa: "—" },
              { ts: "2026-06-24T07:45:00Z", event: "ACCOUNT_DISABLED", user: "suspended@dikdoc.io", ip: "—", mfa: "—" },
              { ts: "2026-06-24T07:32:18Z", event: "ROLE_GRANTED", user: "lab.tech@chu-rabat.ma", ip: "10.0.4.55", mfa: "totp" },
            ].map((e, i) => {
              const tone = e.event.startsWith("LOGIN_FAILED") || e.event.startsWith("ACCOUNT_")
                ? "text-accent"
                : e.event.startsWith("MFA") || e.event.startsWith("PASSWORD")
                  ? "text-[oklch(0.78_0.16_85)]"
                  : "text-success";
              return (
                <div key={i} className="flex items-center gap-3 rounded-lg bg-white/5 p-2 font-mono text-xs">
                  <span className="text-muted-foreground w-44 shrink-0">{e.ts}</span>
                  <span className={"w-32 shrink-0 font-semibold " + tone}>{e.event}</span>
                  <span className="text-foreground/80 flex-1 truncate">{e.user}</span>
                  <span className="text-muted-foreground">{e.ip}</span>
                  <span className="text-primary">{e.mfa}</span>
                </div>
              );
            })}
          </div>
        </CardWrap>
      )}

      {/* Footer */}
      <div className="rounded-lg bg-primary/5 border border-primary/20 p-4 text-xs">
        <p className="font-semibold text-primary">Compliance: ISO 27001 A.9 · Loi 09-08 (CNDP) · HIPAA §164.312(a)</p>
        <p className="text-muted-foreground mt-1">
          Keycloak realm: <code className="text-foreground">dikdoc</code> · OIDC issuer:{" "}
          <code className="text-foreground">https://keycloak.dikdoc.io/realms/dikdoc</code> · MFA required for all
          roles since 2026-06-01.
        </p>
      </div>
    </div>
  );
}
